import { ComponentProps, MouseEvent } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Table, TableHead } from "~/components/ui/table";
import { cn } from "~/lib/utils";
import type { TableSort } from "./TableSort";

/**
 * The dashboard's tables, at one set of metrics. Header size is set here because
 * shadcn takes it from the `table` element, a step larger than every cell.
 */
export const EvalTable = ({ className, ...props }: ComponentProps<typeof Table>) => (
  <Table
    className={cn("[&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:text-xs", className)}
    {...props}
  />
);

/**
 * The same metrics for the health grid, which builds its own `th` and `td`: its
 * run columns are a row of cells inside one cell, and its case column stays
 * stuck to the left edge through a horizontal scroll.
 */
export const EVAL_HEAD_CLS =
  "h-10 px-3 align-middle text-xs font-medium whitespace-nowrap text-muted-foreground";
export const EVAL_CELL_CLS = "px-3 py-2 whitespace-normal";

/**
 * How a grid reads. `dashboard` is the compact set above; `admin` matches the
 * Admin pages' tables — a rounded border around the table, headers at the table's
 * own text size, and a sort icon on every sortable column rather than only the
 * active one. Cell padding is unchanged, so a header stays over its cells.
 */
export type EvalTableVariant = "dashboard" | "admin";
export const ADMIN_HEAD_CLS =
  "h-10 px-3 align-middle font-medium whitespace-nowrap text-muted-foreground";

/**
 * Controls that own their click: the row menu, a tooltip trigger, a score cell
 * in the health grid. A row click that landed on one of these is that control's,
 * not the row's.
 */
const NESTED_CONTROL = "a, button, input, select, textarea";

/**
 * Make a whole row activate its primary action, not just the name inside it.
 *
 * The name stays a real button or link — it is the keyboard path and the
 * accessible name, and the row itself is not focusable. This adds the pointer
 * target around it.
 */
export const rowActivateProps = (
  onActivate: (event: MouseEvent<HTMLElement>) => void,
  className?: string
) => ({
  className: cn("cursor-pointer", className),
  onClick: (event: MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest(NESTED_CONTROL)) {
      return;
    }
    // A drag-select inside this row ends in a click on it, and opening the row
    // over the reader's own highlight is not what they asked for. Read off the
    // drag's own endpoints, so a highlight left anywhere else on the page does
    // not swallow the click — least of all a modifier-click, which in most
    // browsers leaves the highlight standing.
    const selection = window.getSelection();
    const row = event.currentTarget;
    if (
      selection &&
      !selection.isCollapsed &&
      (row.contains(selection.anchorNode) || row.contains(selection.focusNode))
    ) {
      return;
    }
    onActivate(event);
  },
});

interface CaseNameCellProps {
  name: string;
  description: string | null;
  onSelect: () => void;
  /** Off where the whole row already opens the case, so the name needs no hover state of its own. */
  underlineOnHover?: boolean;
}

/**
 * A case's identity, the same wherever a case appears. The second line is the
 * case's description and nothing else — falling back to the tags spent it
 * restating the Tags column beside it.
 */
export const CaseNameCell = ({
  name,
  description,
  onSelect,
  underlineOnHover = true,
}: CaseNameCellProps) => (
  <>
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "block max-w-full cursor-pointer truncate text-left font-mono text-xs font-medium text-foreground",
        underlineOnHover && "hover:underline"
      )}
      title={name}
    >
      {name}
    </button>
    {description && (
      <span
        className="mt-0.5 block max-w-full truncate text-[11px] text-muted-foreground"
        title={description}
      >
        {description}
      </span>
    )}
  </>
);

interface SortableHeadProps<K extends string> {
  columnKey: K;
  label: string;
  sort: TableSort<K>;
  /**
   * Right-aligns a numeric column's header and moves the arrow to the label's
   * left. The arrow holds its space whether or not the column is active, so a
   * trailing one parks 16px between the label and the digits it sits over.
   */
  alignRight?: boolean;
  className?: string;
}

/**
 * The clickable label alone, for a header cell the caller owns — the health
 * grid's sticky `th`s, which are not `TableHead`s.
 */
export const SortButton = <K extends string>({
  columnKey,
  label,
  sort,
  alignRight = false,
  variant = "dashboard",
}: Omit<SortableHeadProps<K>, "className"> & { variant?: EvalTableVariant }) => {
  const isActive = columnKey === sort.sortKey;
  const Icon = sort.direction === "asc" ? ArrowUp : ArrowDown;

  if (variant === "admin") {
    const AdminIcon = isActive ? Icon : ArrowUpDown;

    return (
      <button
        type="button"
        onClick={() => sort.handleSort(columnKey)}
        className={cn(
          "inline-flex cursor-pointer items-center gap-1 select-none",
          alignRight && "flex-row-reverse"
        )}
      >
        {label}
        <AdminIcon className={cn("size-4", !isActive && "text-muted-foreground/50")} aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => sort.handleSort(columnKey)}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1 hover:text-foreground",
        alignRight && "flex-row-reverse",
        isActive && "text-foreground"
      )}
    >
      {label}
      <Icon className={cn("size-3", isActive ? "opacity-100" : "opacity-0")} aria-hidden />
    </button>
  );
};

/** A sortable shadcn column header. */
export const SortableHead = <K extends string>({
  columnKey,
  label,
  sort,
  alignRight = false,
  className,
}: SortableHeadProps<K>) => (
  <TableHead
    className={cn(alignRight && "text-right", className)}
    aria-sort={columnKey === sort.sortKey ? `${sort.direction}ending` : "none"}
  >
    <SortButton columnKey={columnKey} label={label} sort={sort} alignRight={alignRight} />
  </TableHead>
);
